import { jsPDF } from 'jspdf';
import toast from '../../services/toast';
import { INDICATOR_TYPES } from './chatIndicatorTypes';

function stripMarkdown(text) {
  return String(text || '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^\s*[-*+]\s+/gm, '• ')
    .replace(/^\s*\d+\.\s+/gm, (m) => m);
}

function stripPlaceholders(content) {
  return String(content || '')
    .replace(/\[\[CHART\]\]/g, '')
    .replace(/\[\[QUESTION\]\]/g, '')
    .trim();
}

function slugifyFilenamePart(value) {
  const s = String(value || 'conversation')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return s.slice(0, 48) || 'conversation';
}

function formatExportDate() {
  return new Date().toISOString().slice(0, 10);
}

function buildHeaderLines(meta: { threadTitle?: string; agentName?: string } = {}) {
  const title = meta.threadTitle?.trim() || 'Conversation';
  const lines = [`Vanti — ${title}`];
  if (meta.agentName?.trim()) lines.push(`Agent: ${meta.agentName.trim()}`);
  lines.push(`Exported: ${formatExportDate()}`);
  return { title, lines };
}

/**
 * @param {Array<{ role: string, content?: string, streaming?: boolean }>} messages
 */
export function getExportableMessages(messages) {
  if (!Array.isArray(messages)) return [];
  return messages.filter((m) => {
    if (!m || m.streaming || INDICATOR_TYPES.has(m.type)) return false;
    const text = stripPlaceholders(m.content);
    return text.length > 0;
  });
}

/**
 * @param {Array<{ role: string, content?: string }>} messages
 * @param {{ threadTitle?: string, agentName?: string }} [meta]
 */
export function formatChatPlainText(messages, meta: { threadTitle?: string; agentName?: string } = {}) {
  const msgs = getExportableMessages(messages);
  const { lines: headerLines } = buildHeaderLines(meta);
  const body: string[] = [];

  for (const m of msgs) {
    const text = stripMarkdown(stripPlaceholders(m.content));
    if (m.role === 'user') {
      body.push('You:', text, '');
    } else if (m.role === 'assistant') {
      body.push('Vanti:', text, '');
    }
  }

  return [...headerLines, '', ...body].join('\n').trimEnd();
}

/**
 * @param {Array<{ role: string, content?: string }>} messages
 * @param {{ threadTitle?: string, agentName?: string }} [meta]
 */
export function formatChatMarkdown(messages, meta: { threadTitle?: string; agentName?: string } = {}) {
  const msgs = getExportableMessages(messages);
  const { title } = buildHeaderLines(meta);
  const parts = [`# Vanti — ${title}`, ''];

  if (meta.agentName?.trim()) {
    parts.push(`**Agent:** ${meta.agentName.trim()}`, '');
  }
  parts.push(`Exported: ${formatExportDate()}`, '');

  for (const m of msgs) {
    const text = stripPlaceholders(m.content);
    if (m.role === 'user') {
      parts.push('## You', '', text, '');
    } else if (m.role === 'assistant') {
      parts.push('## Vanti', '', text, '');
    }
  }

  return parts.join('\n').trimEnd();
}

/**
 * @param {{ threadTitle?: string, threadId?: string }} [meta]
 */
export function buildChatExportFilename(meta: { threadTitle?: string; threadId?: string } = {}, ext: string) {
  const slug = slugifyFilenamePart(meta.threadTitle || meta.threadId);
  return `vanti-${slug}-${formatExportDate()}.${ext}`;
}

export function downloadTextFile(text, filename, mime = 'text/plain;charset=utf-8') {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function copyTextToClipboard(text, { successMessage = 'Copied to clipboard' } = {}) {
  const value = String(text || '').trim();
  if (!value) {
    toast.error('Nothing to copy');
    return false;
  }
  try {
    await navigator.clipboard.writeText(value);
    if (successMessage) toast.success(successMessage);
    return true;
  } catch {
    toast.error('Could not copy to clipboard');
    return false;
  }
}

/**
 * @param {string} plainText
 * @param {string} filename
 * @param {string} [title]
 */
export function downloadChatPdf(plainText, filename, title = 'Vanti conversation') {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 48;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const maxWidth = pageWidth - margin * 2;
  let y = margin;

  const addPageIfNeeded = (lineHeight) => {
    if (y + lineHeight > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  const titleLines = doc.splitTextToSize(String(title), maxWidth);
  for (const line of titleLines) {
    addPageIfNeeded(18);
    doc.text(line, margin, y);
    y += 18;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  y += 6;

  const bodyLines = doc.splitTextToSize(String(plainText || ''), maxWidth);
  for (const line of bodyLines) {
    addPageIfNeeded(14);
    doc.text(line, margin, y);
    y += 14;
  }

  doc.save(filename);
}

export { stripPlaceholders };
